import { Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UsuarioService } from 'src/app/services/usuario.service';
import { Location } from '@angular/common';
import { TipoDocumento } from 'src/app/models/TipoDocumento';
import { ObraSocial } from 'src/app/models/ObraSocial';
import { Ubicacion } from 'src/app/models/Ubicacion';
import { API_URI } from 'src/environments/environment';
import { AuthService } from 'src/app/services/auth.service';
import { LoadingService } from 'src/app/services/loading.service';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-perfil-cliente',
    templateUrl: './perfil-cliente.component.html',
    styleUrls: ['./perfil-cliente.component.css'],
    standalone: false
})
export class PerfilClienteComponent {
  private _authService = inject(AuthService);
  private _usuarioService = inject(UsuarioService);
  private _loadingService = inject(LoadingService);
  private router = inject(Router);
  private _location = inject(Location);
  private aRoute = inject(ActivatedRoute);
  
  error   = signal<string>('');
  loading = computed(() => this._loadingService.isLoading());

  id: string;

  preview_img_avatar: string = '';
  nombre: string = '';
  tipo_documento: string = '';
  nro_documento: string = '';
  email: string = '';
  telefono: string = '';
  prepaga: string = '';
  plan_prepaga: string = '';
  ubicacion?: Ubicacion;

  constructor(){
      this.id = this.aRoute.snapshot.paramMap.get('id') ?? '';
  }

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;

    // si no vino id, salir
    if (!this.id) {
      this.error.set('Falta el identificador de usuario.');
      this.router.navigate(['/sign-in']);
      return;
    }

    // Si necesitás (re)cargar usuario/cliente desde backend, hacelo bajo el loader:
    await this._loadingService.run(async () => {
      try {
        // Asegura bootstrap de sesión (idempotente, ok en web/nativo)
        // 1) sesión
        await this._authService.ensureSession();

        // 2) cargo usuario + cliente en paralelo
        const [u, c] = await Promise.all([
          firstValueFrom(this._usuarioService.getUsuarioXId(this.id)),
          firstValueFrom(this._usuarioService.getClienteXId(this.id)),
        ]);

        // Si no existe el usuario, no podés editar
        if (!u) {
          this.error.set('El usuario no existe o fue eliminado.');
          this.router.navigate(['/dashboard']);
          return;
        }

         // --- Usuario ---
        this.email = u?.x_email ?? '';
        this.preview_img_avatar = u?.x_img_avatar
          ? `${API_URI}uploads/images/large-${u.x_img_avatar}`
          : '';

        // --- Cliente ---
        const nombre  = c?.x_nombre  ?? '';
        const apellido = c?.x_apellido ?? '';
        this.nombre = (nombre || apellido) ? `${nombre} ${apellido}`.trim() : '';

        // tipos de documento viene como signal
        const tiposDoc = this._usuarioService.tiposDocumentos() ?? [];

        const td = tiposDoc.find((t: TipoDocumento) => t.id === u?.id_tipo_documento);
        this.tipo_documento = td?.x_descripcion ?? '';
        this.nro_documento = u?.x_nro_documento ?? '';
        this.telefono = c?.x_telefono ?? '';

        // obras sociales (mismo criterio: signal, método o propiedad)
        const obras: ObraSocial[] =
          (typeof (this._usuarioService as any).obrasSociales === 'function'
            ? (this._usuarioService as any).obrasSociales()
            : (this._usuarioService as any).obrasSociales) ?? [];

        const os = obras.find((o: ObraSocial) => o.id === c?.id_obra_social);
        this.prepaga = os?.x_descripcion ?? '';
        this.plan_prepaga = c?.x_obra_social_plan ?? '';

        // si manejás ubicaciones por usuario:
        // this.ubicacion = await firstValueFrom(this._usuarioService.getUbicacionDefault(this.id));
      } catch (e: any) {
        console.error(e);
        this.error = e?.message || 'No se pudo cargar el perfil.';
        this.router.navigate(['/sign-in']);
      }
    });
  }

  back(){
    this._location.back();
  }

  whatsapp(){
    const phone = (this.telefono ?? '').trim();
    if (!phone) return;
    const url = `https://api.whatsapp.com/send/?phone=${encodeURIComponent(phone)}&text=&type=phone_number&app_absent=0`;
    window.open(url, '_blank');
  }
}